import React, { useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Collapse,
  Input,
  List,
  Modal,
  Space,
  Tag,
  Typography,
} from "antd";
import useApiCall from "../common/api-call";

const HISTORY_STORAGE_KEY = "debug_scope_data_history";

const DebugScopeData = () => {
  const [tenantId, setTenantId] = useState("");
  const [scopeId, setScopeId] = useState("");
  const [loading, setLoading] = useState(false);
  const [packs, setPacks] = useState([]);
  const [error, setError] = useState(null);
  const [selectedJson, setSelectedJson] = useState(null);
  const [jsonDialogOpen, setJsonDialogOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const { apiCall } = useApiCall();

  const sortedPacks = useMemo(() => {
    return [...packs].sort((a, b) => {
      const aCount = Array.isArray(a?.data) ? a.data.length : 0;
      const bCount = Array.isArray(b?.data) ? b.data.length : 0;
      const aEmpty = aCount === 0;
      const bEmpty = bCount === 0;
      if (aEmpty !== bEmpty) {
        return aEmpty ? 1 : -1;
      }
      if (aCount !== bCount) {
        return bCount - aCount;
      }
      return String(a?.type || "").localeCompare(String(b?.type || ""));
    });
  }, [packs]);

  React.useEffect(() => {
    const savedHistory = localStorage.getItem(HISTORY_STORAGE_KEY);
    if (!savedHistory) {
      return;
    }
    try {
      const parsedHistory = JSON.parse(savedHistory);
      setHistory(Array.isArray(parsedHistory) ? parsedHistory : []);
    } catch (e) {
      setHistory([]);
    }
  }, []);

  const saveToHistory = (tenant, scope) => {
    const normalizedTenant = (tenant || "").trim();
    const normalizedScope = (scope || "").trim();
    if (!normalizedTenant || !normalizedScope) {
      return;
    }
    const newEntry = {
      id: Date.now().toString(),
      tenantId: normalizedTenant,
      scopeId: normalizedScope,
    };
    const exists = history.some(
      item => item.tenantId === normalizedTenant && item.scopeId === normalizedScope,
    );
    if (exists) {
      return;
    }
    const updatedHistory = [newEntry, ...history].slice(0, 10);
    setHistory(updatedHistory);
    localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(updatedHistory));
  };

  const fetchScopeData = async (tenant, scope) => {
    if (!tenant.trim() || !scope.trim() || loading) {
      return;
    }
    setLoading(true);
    setError(null);
    setPacks([]);
    try {
      const response = await apiCall(
        "/debug/fullScope",
        "POST",
        { tenantId: tenant.trim(), scopeId: scope.trim() },
        "admin",
      );
      if (!response || !Array.isArray(response.packs)) {
        setError("Failed to load scope data.");
        return;
      }
      setPacks(response.packs);
      saveToHistory(tenant, scope);
    } catch (e) {
      setError("Failed to load scope data.");
    } finally {
      setLoading(false);
    }
  };

  const handleLoad = async () => {
    await fetchScopeData(tenantId, scopeId);
  };

  const loadFromHistory = async item => {
    const tenant = item?.tenantId || "";
    const scope = item?.scopeId || "";
    setTenantId(tenant);
    setScopeId(scope);
    await fetchScopeData(tenant, scope);
  };

  const openJsonDialog = item => {
    setSelectedJson(item);
    setJsonDialogOpen(true);
  };

  return (
    <div style={{ padding: 24 }}>
      {history.length > 0 && (
        <Card style={{ marginBottom: 24 }}>
          <Typography.Text strong style={{ display: "block", marginBottom: 8 }}>
            Recent Queries
          </Typography.Text>
          <Space wrap>
            {history.map(item => (
              <Tag
                key={item.id}
                onClick={() => loadFromHistory(item)}
                style={{ cursor: "pointer", marginBottom: 8 }}
              >
                {item.tenantId} / {item.scopeId}
              </Tag>
            ))}
          </Space>
        </Card>
      )}

      <Card style={{ marginBottom: 24 }}>
        <Typography.Title level={4} style={{ marginBottom: 8 }}>
          Scope Data Debug
        </Typography.Title>
        <Typography.Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
          Load full scope data and inspect each type.
        </Typography.Text>
        <div style={{ marginBottom: 12 }}>
          <Typography.Text style={{ display: "block", marginBottom: 6 }}>
            Tenant ID
          </Typography.Text>
          <Input
            value={tenantId}
            onChange={e => setTenantId(e.target.value)}
            placeholder="Input tenantId"
          />
        </div>
        <div style={{ marginBottom: 12 }}>
          <Typography.Text style={{ display: "block", marginBottom: 6 }}>
            Scope ID
          </Typography.Text>
          <Input
            value={scopeId}
            onChange={e => setScopeId(e.target.value)}
            placeholder="Input scopeId"
          />
        </div>
        <Button
          type="primary"
          onClick={handleLoad}
          loading={loading}
          disabled={!tenantId.trim() || !scopeId.trim() || loading}
        >
          Load
        </Button>
      </Card>

      {error && (
        <Alert
          type="error"
          message={error}
          style={{ marginBottom: 24 }}
          closable
          onClose={() => setError(null)}
        />
      )}

      {sortedPacks.map(pack => {
        const dataList = Array.isArray(pack?.data) ? pack.data : [];
        const count = dataList.length;
        return (
          <Card
            key={String(pack?.type || "unknown")}
            style={{ marginBottom: 16 }}
            title={
              <Space>
                <Typography.Text strong>{String(pack?.type || "unknown")}</Typography.Text>
                <Tag color={count > 0 ? "blue" : "default"}>{count}</Tag>
              </Space>
            }
          >
            <Collapse
              items={[
                {
                  key: "list",
                  label: "Data List",
                  children: (
                    <List
                      size="small"
                      dataSource={dataList}
                      locale={{ emptyText: "No data" }}
                      renderItem={(item, index) => (
                        <List.Item
                          actions={[
                            <Button
                              key="view"
                              type="link"
                              onClick={() => openJsonDialog(item)}
                            >
                              View JSON
                            </Button>,
                          ]}
                        >
                          <Typography.Text>Item {index + 1}</Typography.Text>
                        </List.Item>
                      )}
                    />
                  ),
                },
              ]}
            />
          </Card>
        );
      })}

      <Modal
        open={jsonDialogOpen}
        onCancel={() => setJsonDialogOpen(false)}
        width="80%"
        title="Item JSON"
        footer={[
          <Button key="close" onClick={() => setJsonDialogOpen(false)}>
            Close
          </Button>,
        ]}
      >
        <pre style={{ overflow: "auto", maxHeight: "60vh" }}>
          {JSON.stringify(selectedJson, null, 2)}
        </pre>
      </Modal>
    </div>
  );
};

export default DebugScopeData;
